import { CircularLoader } from '@modules/common/components/CircularLoader';
import {
  CheckCircleRounded,
  IndeterminateCheckBoxRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { QuestionType, ValidForQuestion } from 'generated/graphql';

const questionTypeToQuestionMap: Record<QuestionType, string> = {
  [QuestionType.FeedbackType]: 'What feedback is being sent?',
  [QuestionType.FeedbackUser]: 'Who has written this feedback?',
  [QuestionType.AnalysisGoal]: 'What is the goal of analyzing this feedback?',
  [QuestionType.Invalid]: '',
};

interface IntegrationDescriptionValidationQuestionsProps {
  validForQuestions: ValidForQuestion[];
  isLoading: boolean;
  isEmptyState: boolean;
  shouldShowAsValidated: boolean;
}

export const IntegrationDescriptionValidationQuestions = ({
  validForQuestions,
  isLoading,
  isEmptyState,
  shouldShowAsValidated,
}: IntegrationDescriptionValidationQuestionsProps) => {
  const renderIcon = (isValid: boolean) => {
    if (isLoading && !shouldShowAsValidated) {
      return <CircularLoader size={20} />;
    }

    if (isEmptyState && !shouldShowAsValidated) {
      return (
        <IndeterminateCheckBoxRounded
          sx={{
            color: 'text.disabled',
            fontSize: '20px',
          }}
        />
      );
    }

    return isValid || shouldShowAsValidated ? (
      <CheckCircleRounded
        sx={{
          color: 'success.light',
          fontSize: '20px',
        }}
      />
    ) : (
      <WarningAmberRounded
        sx={{
          color: 'warning.main',
          fontSize: '20px',
        }}
      />
    );
  };

  const getTextColor = (isValid: boolean) => {
    if (isLoading || isValid || shouldShowAsValidated) {
      return 'text.primary';
    }
    if (isEmptyState) {
      return 'text.secondary';
    }
    return 'warning.main';
  };

  const renderQuestion = (questionType: QuestionType) => {
    const isValid =
      !!validForQuestions.find(
        (validForQuestion) => validForQuestion.question === questionType,
      )?.isValid ?? false;

    const question = questionTypeToQuestionMap[questionType];

    if (!question) {
      return null;
    }

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Stack>{renderIcon(isValid)}</Stack>
        <Typography variant="caption" color={getTextColor(isValid)}>
          {question}
        </Typography>
      </Stack>
    );
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">
        The description should address the following questions:
      </Typography>
      <Stack spacing={1.5} px={1}>
        {renderQuestion(QuestionType.FeedbackType)}
        {renderQuestion(QuestionType.FeedbackUser)}
        {renderQuestion(QuestionType.AnalysisGoal)}
      </Stack>
    </Stack>
  );
};
